import React, { useState, useEffect } from 'react';
import { X, Download, User, Calendar, FileText, CheckCircle, AlertTriangle, XCircle, Send, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AssignmentReviewModal = ({ submission, onClose, onReviewed }) => {
  const [feedback, setFeedback] = useState('');
  const [reviewStatus, setReviewStatus] = useState('approved');
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Download file with proper authentication
  const handleDownload = async (fileUrl, fileName) => {
    if (!fileUrl) return;
    
    setDownloading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Build the full URL
      let downloadUrl = fileUrl;
      if (fileUrl.startsWith('/api')) {
        downloadUrl = `${API_URL}${fileUrl}`;
      }
      
      // Add token as query param if not already present
      const separator = downloadUrl.includes('?') ? '&' : '?';
      downloadUrl = `${downloadUrl}${separator}token=${token}`;
      
      // Fetch the file with auth
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to download file');
      }
      
      // Get the blob and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'submission';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.message || 'Failed to download file. The student may need to re-submit.');
    } finally {
      setDownloading(false);
    }
  };

  const submitReview = async () => {
    if (!feedback.trim()) {
      toast.error('Please provide feedback');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/assignments/submissions/${submission.id}/review`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          feedback: feedback,
          review_status: reviewStatus
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Assignment ${reviewStatus === 'approved' ? 'approved' : reviewStatus === 'needs_revision' ? 'sent back for revision' : 'rejected'}!`);
        onReviewed && onReviewed();
        onClose();
      } else {
        toast.error(data.detail || 'Failed to submit review');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-t-xl">
          <div>
            <h2 className="text-xl font-bold">Review Assignment</h2>
            <p className="text-orange-100 text-sm">{submission?.assignment_title || 'Assignment'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Submission Info */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{submission?.user_name || 'Student'}</p>
                <p className="text-sm text-gray-500">{submission?.user_email}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4" />
              <span>Submitted: {formatDate(submission?.submitted_at)}</span>
            </div>
            
            {submission?.submission_file_url && (
              <button
                onClick={() => handleDownload(
                  submission.submission_file_url, 
                  submission.submission_file_name || 'submission'
                )}
                disabled={downloading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors w-fit disabled:opacity-50"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {downloading ? 'Downloading...' : 'Download Submitted File'}
              </button>
            )}
          </div>

          {/* Student Notes */}
          {submission?.submission_notes && (
            <div className="bg-amber-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-amber-800">Student Notes</span>
              </div>
              <p className="text-gray-700 whitespace-pre-wrap">{submission.submission_notes}</p>
            </div>
          )}

          {/* Review Status Selection */}
          <div className="space-y-3">
            <label className="block font-medium text-gray-900">Review Decision</label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setReviewStatus('approved')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  reviewStatus === 'approved'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <CheckCircle className={`w-6 h-6 mx-auto mb-2 ${reviewStatus === 'approved' ? 'text-green-500' : 'text-gray-400'}`} />
                <p className="font-medium">Approve</p>
                <p className="text-xs text-gray-500">Well done!</p>
              </button>
              
              <button
                type="button"
                onClick={() => setReviewStatus('needs_revision')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  reviewStatus === 'needs_revision'
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-gray-200 hover:border-amber-300'
                }`}
              >
                <AlertTriangle className={`w-6 h-6 mx-auto mb-2 ${reviewStatus === 'needs_revision' ? 'text-amber-500' : 'text-gray-400'}`} />
                <p className="font-medium">Revision</p>
                <p className="text-xs text-gray-500">Needs work</p>
              </button>
              
              <button
                type="button"
                onClick={() => setReviewStatus('rejected')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  reviewStatus === 'rejected'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 hover:border-red-300'
                }`}
              >
                <XCircle className={`w-6 h-6 mx-auto mb-2 ${reviewStatus === 'rejected' ? 'text-red-500' : 'text-gray-400'}`} />
                <p className="font-medium">Reject</p>
                <p className="text-xs text-gray-500">Not accepted</p>
              </button>
            </div>
          </div>

          {/* Feedback */}
          <div className="space-y-2">
            <label className="block font-medium text-gray-900">Your Feedback</label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={
                reviewStatus === 'approved' 
                  ? 'Great work! You demonstrated excellent understanding...'
                  : reviewStatus === 'needs_revision'
                  ? 'Please review the following areas and resubmit...'
                  : 'This submission does not meet the requirements because...'
              }
              className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
              rows={5}
            />
            <p className="text-xs text-gray-500">
              This feedback will be sent to the student via email and in-app notification.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={submitReview}
            disabled={submitting || !feedback.trim()}
            className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
              reviewStatus === 'approved'
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : reviewStatus === 'needs_revision'
                ? 'bg-amber-600 hover:bg-amber-700 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'
            } disabled:opacity-50`}
          >
            {submitting ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Submit Review
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignmentReviewModal;
